import type { VirtualElement } from '@popperjs/core'
import type { CommandMeta } from './command-meta.js'

export interface CommandStreamState {
  /** Cumulative content rendered so far. */
  content: string
  /** True while more content is expected; false on completion or terminal error. */
  pending: boolean
  /**
   * Terminal error. When set, `pending` MUST be false. `message` is shown to the user as-is, so it
   * must be user-facing copy, never a raw error.
   */
  error?: { message: string }
}

/**
 * React hook that subscribes a component to a streaming producer for the
 * component's lifetime. MUST follow Rules of Hooks. Unmount MUST cancel the
 * producer (RTK Query subscription, AbortController, etc.).
 */
export type UseCommandStream<TStreamParams> = (params: TStreamParams) => CommandStreamState

export type StreamingCommandFn<TFormValues, TStreamParams> = (
  formValues: TFormValues & { anchorEl?: HTMLElement | VirtualElement | null }
) => Promise<{ params: TStreamParams }>

/**
 * Command variant that produces incremental, streamed output instead of a
 * single result. The popup runs `cmd(formValues)` to obtain `params`, then
 * mounts `<StreamingResponse>` which calls `useStream(params)` for the
 * component's lifetime.
 *
 * Two type parameters because the form's input shape (`TFormValues`, validated
 * by `cmdParamsSchema`) is often different from the stream producer's input
 * shape (`TStreamParams` — e.g. a payload enriched with `requestId`).
 */
export interface StreamingCommand<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TFormValues extends any = any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TStreamParams extends any = any,
> extends CommandMeta<TFormValues> {
  /** Discriminator. Required literal-true so the menu renders `<StreamingResponse>`. */
  streamResult: true
  /**
   * Resolves form values to the params `useStream` should be called with. Async
   * so the producer can do auth round-trips (e.g. resolve agent, mint requestId)
   * before the popup commits to streaming. Throws → popup shows error state.
   */
  cmd: StreamingCommandFn<TFormValues, TStreamParams>
  /**
   * Hook invoked inside `<StreamingResponse>`. The popup passes the params
   * returned from `cmd` and renders the returned `CommandStreamState`.
   * Cancellation = unmounting the consumer.
   */
  useStream: UseCommandStream<TStreamParams>
}

/**
 * Discriminator for the `Command | StreamingCommand` union. Accepts any
 * `CommandMeta` shape (the common base) so callers can pass a regular
 * `Command`, a `StreamingCommand`, or any narrowed superset — without
 * forcing this module to reach back into `plugin-types.tsx` (which would
 * recreate the circular import this module exists to avoid).
 */
export function isStreamingCommand<T extends CommandMeta>(command: T | StreamingCommand): command is StreamingCommand {
  return (command as StreamingCommand).streamResult === true
}
